import React, { useState, useEffect, useCallback } from 'react';
import contentService from '../services/contentService';
import './DiscoveryPage.css';

import Loading from '../components/Loading';
// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(n);
};

// Extrae el shortcode de una URL de Instagram (/p/XXX/, /reel/XXX/, /tv/XXX/)
const shortcode = (url) => {
  if (!url) return null;
  const m = url.match(/\/(p|reel|tv)\/([A-Za-z0-9_-]+)/);
  return m ? m[2] : null;
};

const engagementRate = (likes, comments, followers) => {
  if (!followers) return null;
  const rate = ((likes + comments) / followers) * 100;
  return rate.toFixed(2) + '%';
};

// Detecta posts patrocinados por hashtags/keywords en caption
const PAID_KEYWORDS = [
  '#ad', '#publicidad', '#sponsored', '#patrocinado', '#collab',
  '#colaboracion', '#colaboración', '#gifted', '#promo', '#partnership',
  '#instapublicidad', '#publi', '#publicado', 'contenido patrocinado',
  'en colaboración con', 'en colaboracion con',
];
const isPaidPost = (caption = '') => {
  const lower = caption.toLowerCase();
  return PAID_KEYWORDS.some(kw => lower.includes(kw));
};

// Extrae #hashtags de los captions y devuelve ranking por frecuencia
const extractHashtags = (posts = []) => {
  const counts = {};
  for (const post of posts) {
    const caption = post.caption || '';
    // Soporta letras con tilde y caracteres latinos
    const tags = caption.match(/#([\wÀ-ɏḀ-ỿ]+)/gu);
    if (!tags) continue;
    const unique = [...new Set(tags.map(t => t.slice(1).toLowerCase()))];
    for (const t of unique) counts[t] = (counts[t] || 0) + 1;
  }
  return Object.entries(counts)
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20);
};

// Extrae @menciones de los captions y devuelve ranking por frecuencia
const extractMenciones = (posts = []) => {
  const counts = {};
  let totalPosts = 0;
  for (const post of posts) {
    const caption = post.caption || '';
    const mentions = caption.match(/@([\w.]+)/g);
    if (!mentions) continue;
    totalPosts++;
    const unique = [...new Set(mentions.map(m => m.slice(1).toLowerCase()))];
    for (const m of unique) {
      counts[m] = (counts[m] || 0) + 1;
    }
  }
  if (!totalPosts) return [];
  return Object.entries(counts)
    .map(([username, n]) => ({ username, count: n, pct: ((n / posts.length) * 100).toFixed(1) }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
};

// ── sub-componentes ───────────────────────────────────────────────────────────
const STAT_ICONS = {
  'Seguidores':           'bi-people-fill',
  'Siguiendo':            'bi-person-check-fill',
  'Posts':                'bi-grid-fill',
  'Interacciones medias': 'bi-hand-thumbs-up-fill',
  'Eng. rate':            'bi-bullseye',
};

function StatCard({ label, value, icon }) {
  const iconClass = icon || STAT_ICONS[label] || 'bi-bar-chart-fill';
  return (
    <div className="disc-stat">
      <div className="disc-stat__icon">
        <i className={`bi ${iconClass}`} />
      </div>
      <span className="disc-stat__value">{value}</span>
      <span className="disc-stat__label">{label}</span>
    </div>
  );
}

function PostThumb({ post }) {
  const [imgErr, setImgErr] = useState(false);
  const isVideo = post.media_type === 'VIDEO' || post.media_type === 'REELS';
  const rawSrc = isVideo
    ? (post.thumbnail_url || post.media_url)
    : (post.media_url || post.thumbnail_url);
  const src = !imgErr && rawSrc ? rawSrc : null;

  const views   = post._views   || null;
  const collabs = post._collabs || null;

  const metrics = [
    post.like_count     != null ? { icon: 'bi-heart-fill',       val: fmt(post.like_count)     } : null,
    post.comments_count != null ? { icon: 'bi-chat-fill',        val: fmt(post.comments_count) } : null,
    views               >  0   ? { icon: 'bi-eye-fill',          val: fmt(views)               } : null,
  ].filter(Boolean);

  return (
    <a className="disc-post" href={post.permalink} target="_blank" rel="noreferrer">
      <div className="disc-post__thumb">
        {src ? (
          <img src={src} alt="" onError={() => setImgErr(true)} />
        ) : (
          <div className="disc-post__placeholder">
            {isVideo ? <i className="bi bi-play-circle" /> : <i className="bi bi-image" />}
          </div>
        )}
        {isVideo && <span className="disc-post__badge">▶</span>}
        {collabs && collabs.length > 0 && (
          <div className="disc-post__collab-overlay">
            <span className="disc-post__collab-badge">
              <i className="bi bi-people-fill" /> Collab
            </span>
            {collabs.map(c => (
              <span key={c.username} className="disc-post__collab-user">@{c.username}</span>
            ))}
          </div>
        )}
      </div>
      {metrics.length > 0 && (
        <div className="disc-post__meta">
          {metrics.map((m, i) => (
            <span key={i} className="disc-post__metric">
              <i className={`bi ${m.icon}`} /> {m.val}
            </span>
          ))}
        </div>
      )}
    </a>
  );
}

function MencionesSection({ menciones, totalPosts }) {
  const max = menciones.length ? menciones[0].count : 0;
  return (
    <div className="disc-paid-section">
      <h3 className="disc-posts-title">
        <i className="bi bi-at" /> Etiquetas de cuentas
        <span className={`disc-paid-badge ${menciones.length > 0 ? 'disc-paid-badge--found' : 'disc-paid-badge--none'}`}>
          {menciones.length} cuenta{menciones.length !== 1 ? 's' : ''} mencionada{menciones.length !== 1 ? 's' : ''}
        </span>
      </h3>
      {menciones.length === 0 ? (
        <p className="disc-saved-empty">No se detectaron @menciones en los captions de la muestra.</p>
      ) : (
        <div className="disc-menciones-list">
          {menciones.map(({ username, count, pct }) => (
            <div key={username} className="disc-mencion-row">
              <a
                className="disc-mencion-user"
                href={`https://instagram.com/${username}`}
                target="_blank"
                rel="noreferrer"
              >
                @{username}
              </a>
              <div className="disc-mencion-bar-wrap">
                <div className="disc-mencion-bar" style={{ width: `${(count / max) * 100}%` }} />
              </div>
              <span className="disc-mencion-pct">{pct}%</span>
              <span className="disc-mencion-count">{count} post{count !== 1 ? 's' : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ApifyResults({ data }) {
  const items = data.items || [];
  const collabs    = items.filter(p => p.co_authors?.length > 0);
  const sponsored  = items.filter(p => p.is_sponsored);
  const withViews  = items.filter(p => (p.views || p.plays) > 0);
  const withShares = items.filter(p => p.shares > 0);

  const reels      = items.filter(p => (p.type || '').toLowerCase().includes('reel') || (p.type || '').toLowerCase().includes('video'));

  const avgLikes   = items.length ? Math.round(items.reduce((a, p) => a + (p.likes || 0), 0) / items.length) : 0;
  const avgComments= items.length ? Math.round(items.reduce((a, p) => a + (p.comments || 0), 0) / items.length) : 0;
  const avgViews   = withViews.length ? Math.round(withViews.reduce((a, p) => a + (p.views || p.plays || 0), 0) / withViews.length) : 0;
  const avgShares  = withShares.length ? Math.round(withShares.reduce((a, p) => a + p.shares, 0) / withShares.length) : 0;

  // KPIs derivados
  const reelsWithViews = reels.filter(p => (p.views || p.plays) > 0);
  const avgReelViews   = reelsWithViews.length ? Math.round(reelsWithViews.reduce((a, p) => a + (p.views || p.plays || 0), 0) / reelsWithViews.length) : 0;
  const totalEngViews  = withViews.reduce((a, p) => a + (p.likes || 0) + (p.comments || 0), 0);
  const totalViews     = withViews.reduce((a, p) => a + (p.views || p.plays || 0), 0);
  const engByViews     = totalViews > 0 ? ((totalEngViews / totalViews) * 100).toFixed(2) : null;

  return (
    <div className="disc-apify-results">
      {/* Resumen */}
      <div className="disc-apify-summary">
        <div className="disc-apify-kpi">
          <span className="disc-apify-kpi__val">{data.total}</span>
          <span className="disc-apify-kpi__lbl">posts analizados</span>
        </div>
        <div className="disc-apify-kpi">
          <span className="disc-apify-kpi__val">{fmt(avgLikes)}</span>
          <span className="disc-apify-kpi__lbl">likes promedio</span>
        </div>
        <div className="disc-apify-kpi">
          <span className="disc-apify-kpi__val">{fmt(avgComments)}</span>
          <span className="disc-apify-kpi__lbl">comentarios promedio</span>
        </div>
        {avgViews > 0 && (
          <div className="disc-apify-kpi">
            <span className="disc-apify-kpi__val">{fmt(avgViews)}</span>
            <span className="disc-apify-kpi__lbl">est. vistas por post</span>
          </div>
        )}
        {avgReelViews > 0 && (
          <div className="disc-apify-kpi">
            <span className="disc-apify-kpi__val">{fmt(avgReelViews)}</span>
            <span className="disc-apify-kpi__lbl">prom. vistas de reels</span>
          </div>
        )}
        {engByViews !== null && (
          <div className="disc-apify-kpi">
            <span className="disc-apify-kpi__val">{engByViews}%</span>
            <span className="disc-apify-kpi__lbl">eng. por visualizaciones</span>
          </div>
        )}
        {avgShares > 0 && (
          <div className="disc-apify-kpi">
            <span className="disc-apify-kpi__val">{fmt(avgShares)}</span>
            <span className="disc-apify-kpi__lbl">compartidos promedio</span>
          </div>
        )}
        <div className="disc-apify-kpi disc-apify-kpi--alert">
          <span className="disc-apify-kpi__val">{collabs.length}</span>
          <span className="disc-apify-kpi__lbl">posts collab</span>
        </div>
        <div className="disc-apify-kpi disc-apify-kpi--warn">
          <span className="disc-apify-kpi__val">{sponsored.length}</span>
          <span className="disc-apify-kpi__lbl">patrocinados</span>
        </div>
      </div>

      {/* Posts Collab */}
      <div className="disc-paid-section">
        <h3 className="disc-posts-title">
          <i className="bi bi-people-fill" /> Posts Collab
          <span className={`disc-paid-badge ${collabs.length > 0 ? 'disc-paid-badge--found' : 'disc-paid-badge--none'}`}>
            {collabs.length} de {items.length} posts
          </span>
        </h3>
        {collabs.length === 0
          ? <p className="disc-saved-empty">No se detectaron posts en colaboración.</p>
          : <div className="disc-paid-list">
              {collabs.map(p => (
                <a key={p.id} className="disc-paid-item" href={p.permalink} target="_blank" rel="noreferrer">
                  {p.thumbnail && <img src={p.thumbnail} alt="" className="disc-paid-thumb" />}
                  <div className="disc-paid-info">
                    <div className="disc-collab-accounts">
                      {p.co_authors.map(c => (
                        <span key={c.id || c.username} className="disc-collab-chip">
                          <i className="bi bi-person-fill" /> @{c.username}
                        </span>
                      ))}
                    </div>
                    <p className="disc-paid-caption">{(p.caption || '').slice(0, 100)}{p.caption?.length > 100 ? '…' : ''}</p>
                    <div className="disc-paid-meta">
                      {p.likes > 0 && <span>{fmt(p.likes)} ❤</span>}
                      {p.comments > 0 && <span>{fmt(p.comments)} 💬</span>}
                      {(p.views || p.plays) > 0 && <span>{fmt(p.views || p.plays)} 👁</span>}
                    </div>
                  </div>
                  <i className="bi bi-box-arrow-up-right disc-paid-link" />
                </a>
              ))}
            </div>
        }
      </div>

      {/* Posts patrocinados (dato confiable de Apify) */}
      <div className="disc-paid-section">
        <h3 className="disc-posts-title">
          <i className="bi bi-megaphone-fill" /> Patrocinados confirmados
          <span className={`disc-paid-badge ${sponsored.length > 0 ? 'disc-paid-badge--found' : 'disc-paid-badge--none'}`}>
            {sponsored.length} de {items.length} posts
          </span>
        </h3>
        {sponsored.length === 0
          ? <p className="disc-saved-empty">No se detectaron posts con marca de patrocinado.</p>
          : <div className="disc-paid-list">
              {sponsored.map(p => (
                <a key={p.id} className="disc-paid-item" href={p.permalink} target="_blank" rel="noreferrer">
                  {p.thumbnail && <img src={p.thumbnail} alt="" className="disc-paid-thumb" />}
                  <div className="disc-paid-info">
                    <p className="disc-paid-caption">{(p.caption || '').slice(0, 120)}{p.caption?.length > 120 ? '…' : ''}</p>
                    <div className="disc-paid-meta">
                      {p.likes > 0 && <span>{fmt(p.likes)} ❤</span>}
                      {p.comments > 0 && <span>{fmt(p.comments)} 💬</span>}
                      {(p.views || p.plays) > 0 && <span>{fmt(p.views || p.plays)} 👁</span>}
                      {p.shares > 0 && <span>{fmt(p.shares)} 🔁</span>}
                    </div>
                  </div>
                  <i className="bi bi-box-arrow-up-right disc-paid-link" />
                </a>
              ))}
            </div>
        }
      </div>

    </div>
  );
}


function HashtagsSection({ hashtags }) {
  if (!hashtags.length) return null;
  const max = hashtags[0].count;
  return (
    <div className="disc-menciones">
      <h3 className="disc-posts-title">
        <i className="bi bi-hash" /> Hashtags utilizados
      </h3>
      <div className="disc-menciones-list">
        {hashtags.map(({ tag, count }) => (
          <div key={tag} className="disc-mencion-row">
            <a
              className="disc-mencion-user"
              href={`https://instagram.com/explore/tags/${tag}`}
              target="_blank"
              rel="noreferrer"
            >
              #{tag}
            </a>
            <div className="disc-mencion-bar-wrap">
              <div className="disc-mencion-bar disc-mencion-bar--tag" style={{ width: `${(count / max) * 100}%` }} />
            </div>
            <span className="disc-mencion-count">{count}×</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const MONTH_NAMES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function LikesChart({ posts }) {
  // Agrupa likes por mes (YYYY-MM) y suma
  const byMonth = {};
  for (const p of posts) {
    if (!p.timestamp || p.like_count == null) continue;
    const d = new Date(p.timestamp);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!byMonth[key]) byMonth[key] = { likes: 0, posts: 0, year: d.getFullYear(), month: d.getMonth() };
    byMonth[key].likes += p.like_count;
    byMonth[key].posts += 1;
  }

  const months = Object.entries(byMonth)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, v]) => ({ key, ...v }));

  if (months.length < 1) return null;

  const max = Math.max(...months.map(m => m.likes));
  const W = 560, H = 190;
  const PAD = { top: 28, right: 12, bottom: 40, left: 52 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const gap = chartW / months.length;
  const barW = Math.max(16, Math.min(48, gap * 0.6));

  const guides = [0.25, 0.5, 0.75, 1].map(f => ({
    y: PAD.top + chartH - f * chartH,
    label: fmt(Math.round(max * f)),
  }));

  return (
    <div className="disc-likes-chart">
      <h3 className="disc-posts-title">
        <i className="bi bi-graph-up" /> Evolución de likes por mes
        <span className="disc-chart-note">total de likes sumados por mes · {months.length} {months.length === 1 ? 'mes' : 'meses'}</span>
      </h3>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', overflow: 'visible' }}>
        {guides.map(g => (
          <g key={g.y}>
            <line x1={PAD.left} y1={g.y} x2={W - PAD.right} y2={g.y} stroke="#e8eae8" strokeWidth="1" strokeDasharray="3,3" />
            <text x={PAD.left - 6} y={g.y + 4} textAnchor="end" fontSize="10" fill="#9ab0aa">{g.label}</text>
          </g>
        ))}
        <line x1={PAD.left} y1={PAD.top + chartH} x2={W - PAD.right} y2={PAD.top + chartH} stroke="#d0d8d6" strokeWidth="1" />

        {months.map((m, i) => {
          const cx = PAD.left + i * gap + gap / 2;
          const barH = max ? (m.likes / max) * chartH : 0;
          const barX = cx - barW / 2;
          const barY = PAD.top + chartH - barH;
          const label = `${MONTH_NAMES[m.month]} ${String(m.year).slice(2)}`;
          return (
            <g key={m.key}>
              <rect x={barX} y={barY} width={barW} height={barH} fill="#71d234" rx="3" opacity="0.85" />
              <text x={cx} y={barY - 6} textAnchor="middle" fontSize="10" fill="#002421" fontWeight="700">
                {fmt(m.likes)}
              </text>
              <text x={cx} y={PAD.top + chartH + 14} textAnchor="middle" fontSize="9" fill="#7a9590">{label}</text>
              <text x={cx} y={PAD.top + chartH + 25} textAnchor="middle" fontSize="8" fill="#b0c4be">{m.posts}p</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function SavedCard({ perfil, onDelete, onView }) {
  return (
    <div className="disc-saved-card disc-saved-card--clickable" onClick={() => onView(perfil.username)}>
      <div className="disc-saved-card__avatar">
        {perfil.foto_perfil
          ? <img src={perfil.foto_perfil} alt={perfil.username} />
          : <i className="bi bi-person-circle" />}
      </div>
      <div className="disc-saved-card__info">
        <span className="disc-saved-card__username">@{perfil.username}</span>
        {perfil.nombre && <span className="disc-saved-card__nombre">{perfil.nombre}</span>}
        <div className="disc-saved-card__stats">
          <span>{fmt(perfil.seguidores)} seg.</span>
          <span>·</span>
          <span>{fmt(perfil.num_posts)} posts</span>
        </div>
      </div>
      <div className="disc-saved-card__actions">
        <span className="disc-saved-card__view-hint">
          <i className="bi bi-graph-up-arrow" /> Ver datos
        </span>
        <button
          className="disc-saved-card__del"
          title="Eliminar"
          onClick={(e) => { e.stopPropagation(); onDelete(perfil.id); }}
        >
          <i className="bi bi-trash3" />
        </button>
      </div>
    </div>
  );
}

// ── componente principal ──────────────────────────────────────────────────────
export default function DiscoveryPage() {
  const [query, setQuery]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState('');
  const [saving, setSaving]       = useState(false);
  const [savedMsg, setSavedMsg]   = useState('');
  const [saved, setSaved]         = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [apifyData, setApifyData] = useState(null);
  const [apifyLoading, setApifyLoading] = useState(false);
  const [apifyError, setApifyError]     = useState('');

  // Cargar guardados al montar
  useEffect(() => {
    contentService.getDiscoverySaved()
      .then(r => setSaved(r.data))
      .catch(() => {})
      .finally(() => setLoadingSaved(false));
  }, []);

  const isAlreadySaved = result
    ? saved.some(p => p.username === result.username)
    : false;

  const searchByUsername = useCallback(async (username) => {
    if (!username) return;
    setLoading(true);
    setError('');
    setResult(null);
    setSavedMsg('');
    // Scroll al top del resultado
    window.scrollTo({ top: 0, behavior: 'smooth' });
    try {
      const { data } = await contentService.getIGBusinessDiscovery({
        username,
        include_media: true,
        media_limit: 30,
      });
      if (data.error) {
        setError(data.error?.message || 'No se pudo obtener el perfil.');
      } else if (!data.username) {
        setError('No se encontró el perfil. Verifica que la cuenta exista y sea de tipo Business o Creator en Instagram.');
      } else {
        setResult(data);
      }
    } catch (err) {
      const d = err?.response?.data;
      const msg = d?.message
        || d?.error?.message
        || d?.detail
        || 'Error al buscar el perfil.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    const username = query.trim().replace(/^@+/, '');
    await searchByUsername(username);
  }, [query, searchByUsername]);

  const handleSave = async () => {
    if (!result) return;
    setSaving(true);
    setSavedMsg('');
    try {
      await contentService.saveDiscoveryPerfil({
        username:    result.username,
        nombre:      result.name || '',
        biografia:   result.biography || '',
        foto_perfil: result.profile_picture_url || '',
        sitio_web:   result.website || '',
        seguidores:  result.followers_count,
        siguiendo:   result.follows_count,
        num_posts:   result.media_count,
      });
      setSavedMsg('¡Guardado!');
      // Recargar lista
      const { data } = await contentService.getDiscoverySaved();
      setSaved(data);
    } catch {
      setSavedMsg('Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await contentService.deleteDiscoveryPerfil(id);
      setSaved(prev => prev.filter(p => p.id !== id));
    } catch {}
  };

  const handleApifyAnalysis = async () => {
    if (!result?.username) return;
    setApifyLoading(true);
    setApifyError('');
    setApifyData(null);
    try {
      const { data } = await contentService.getDiscoveryApify(result.username);
      setApifyData(data);
    } catch (err) {
      const d = err?.response?.data;
      setApifyError(d?.detail || 'Error al obtener análisis extra.');
    } finally {
      setApifyLoading(false);
    }
  };

  // Posts base de Business Discovery
  const bdPosts = result?.media?.data || [];

  // Merge con Apify: enriquece métricas faltantes y agrega posts que BD no trajo
  const posts = (() => {
    if (!apifyData?.items?.length) return bdPosts;

    // Mapa apify por shortcode
    const apMap = {};
    for (const item of apifyData.items) {
      const sc = shortcode(item.permalink);
      if (sc) apMap[sc] = item;
    }

    // Enriquecer posts de BD con datos de Apify
    const bdShortcodes = new Set();
    const enriched = bdPosts.map(p => {
      const sc = shortcode(p.permalink);
      if (sc) bdShortcodes.add(sc);
      const ap = sc ? apMap[sc] : null;
      if (!ap) return p;
      return {
        ...p,
        like_count:     p.like_count     ?? ap.likes    ?? null,
        comments_count: p.comments_count ?? ap.comments ?? null,
        _views:   ap.views || ap.plays || null,
        _collabs: ap.co_authors?.length > 0 ? ap.co_authors : null,
      };
    });

    // Posts que Apify trajo pero BD no tiene
    const apifyOnly = apifyData.items
      .filter(item => {
        const sc = shortcode(item.permalink);
        return sc && !bdShortcodes.has(sc);
      })
      .map(item => {
        const isVid = ['video','reel'].some(t => (item.type || '').toLowerCase().includes(t));
        return {
          id:             item.id || shortcode(item.permalink),
          media_type:     isVid ? 'VIDEO' : 'IMAGE',
          media_url:      item.thumbnail,
          thumbnail_url:  item.thumbnail,
          permalink:      item.permalink,
          caption:        item.caption || '',
          timestamp:      item.timestamp || '',
          like_count:     item.likes    ?? null,
          comments_count: item.comments ?? null,
          _views:   item.views || item.plays || null,
          _collabs: item.co_authors?.length > 0 ? item.co_authors : null,
          _apify_only: true,
        };
      });

    return [...enriched, ...apifyOnly]
      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  })();

  // KPIs calculados sobre posts enriquecidos
  const menciones = extractMenciones(posts);
  const hashtags = extractHashtags(posts);
  const paidPosts = posts.filter(p => isPaidPost(p.caption || ''));
  const postsWithEngagement = posts.filter(p => p.like_count != null || p.comments_count != null);
  const avgInteracciones = postsWithEngagement.length
    ? Math.round(postsWithEngagement.reduce((a, p) => a + (p.like_count || 0) + (p.comments_count || 0), 0) / postsWithEngagement.length)
    : null;

  const avgEng = posts.length
    ? engagementRate(
        posts.reduce((a, p) => a + (p.like_count || 0), 0) / posts.length,
        posts.reduce((a, p) => a + (p.comments_count || 0), 0) / posts.length,
        result?.followers_count
      )
    : null;

  // KPIs enriquecidos con Apify (solo cuando hay datos)
  const postsWithLikes    = apifyData ? posts.filter(p => p.like_count > 0)     : [];
  const postsWithComments = apifyData ? posts.filter(p => p.comments_count > 0) : [];
  const postsWithViews    = apifyData ? posts.filter(p => p._views > 0)         : [];
  const reelPosts         = apifyData ? posts.filter(p => p.media_type === 'VIDEO' && p._views > 0) : [];

  const apifyAvgLikes    = postsWithLikes.length    ? Math.round(postsWithLikes.reduce((a, p)    => a + p.like_count,     0) / postsWithLikes.length)    : null;
  const apifyAvgComments = postsWithComments.length ? Math.round(postsWithComments.reduce((a, p) => a + p.comments_count, 0) / postsWithComments.length) : null;
  const apifyAvgViews    = postsWithViews.length    ? Math.round(postsWithViews.reduce((a, p)    => a + p._views,         0) / postsWithViews.length)    : null;
  const apifyAvgReels    = reelPosts.length         ? Math.round(reelPosts.reduce((a, p)         => a + p._views,         0) / reelPosts.length)         : null;

  return (
    <div className="disc-page">
      {/* Header */}
      <div className="disc-header">
        <h1 className="disc-title">
          <i className="bi bi-search-heart" /> Discovery
        </h1>
        <p className="disc-subtitle">Busca perfiles de Instagram y guarda los que te interesan</p>
      </div>

      {/* Buscador */}
      <form className="disc-search-row" onSubmit={handleSearch}>
        <div className="disc-search-input-wrap">
          <span className="disc-search-at">@</span>
          <input
            className="disc-search-input"
            type="text"
            placeholder="usuario de Instagram"
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck="false"
          />
        </div>
        <button
          className="disc-search-btn"
          type="submit"
          disabled={loading || !query.trim()}
        >
          {loading ? <span className="disc-spinner" /> : <><i className="bi bi-search" /> Buscar</>}
        </button>
      </form>

      {/* Error */}
      {error && (
        <div className="disc-error">
          <i className="bi bi-exclamation-triangle" /> {error}
        </div>
      )}

      {/* Resultado */}
      {result && (
        <div className="disc-result">
          {/* Perfil header */}
          <div className="disc-profile">
            <div className="disc-profile__avatar">
              {result.profile_picture_url
                ? <img src={result.profile_picture_url} alt={result.username} />
                : <i className="bi bi-person-circle" />}
            </div>
            <div className="disc-profile__info">
              <div className="disc-profile__top">
                <h2 className="disc-profile__username">
                  <a
                    href={`https://instagram.com/${result.username}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    @{result.username}
                  </a>
                  {apifyData?.is_verified && (
                    <span className="disc-verified" title="Cuenta verificada">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L14.09 8.26L21 9.27L16.18 13.97L17.45 21L12 17.77L6.55 21L7.82 13.97L3 9.27L9.91 8.26L12 2Z" fill="#3897f0" stroke="#3897f0" strokeWidth="1.5" strokeLinejoin="round"/>
                        <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  )}
                </h2>
                <div className="disc-profile__actions">
                  <button
                    className={`disc-save-btn${isAlreadySaved ? ' disc-save-btn--saved' : ''}`}
                    onClick={handleSave}
                    disabled={saving || isAlreadySaved}
                  >
                    {saving
                      ? <span className="disc-spinner" />
                      : isAlreadySaved
                        ? <><i className="bi bi-bookmark-check-fill" /> Guardado</>
                        : <><i className="bi bi-bookmark-plus" /> Guardar</>}
                  </button>
                  <button
                    className="disc-apify-btn"
                    onClick={handleApifyAnalysis}
                    disabled={apifyLoading}
                    title="Obtiene collabs, patrocinados, visualizaciones y más datos que Meta no expone"
                  >
                    {apifyLoading
                      ? <><span className="disc-spinner" /> Analizando…</>
                      : <><i className="bi bi-stars" /> {apifyData ? 'Re-analizar' : 'Análisis extra'}</>}
                  </button>
                </div>
              </div>
              {result.name && <p className="disc-profile__name">{result.name}</p>}
              {result.biography && <p className="disc-profile__bio">{result.biography}</p>}
              {result.website && (
                <a className="disc-profile__web" href={result.website} target="_blank" rel="noreferrer">
                  <i className="bi bi-link-45deg" /> {result.website}
                </a>
              )}
              {apifyData?.profile && (
                <div className="disc-profile__extra">
                  {apifyData.profile.business_category && (
                    <span className="disc-profile__tag">
                      <i className="bi bi-tag-fill" /> {apifyData.profile.business_category}
                    </span>
                  )}
                  {apifyData.profile.is_business_account && (
                    <span className="disc-profile__tag disc-profile__tag--business">
                      <i className="bi bi-briefcase-fill" /> Cuenta de empresa
                    </span>
                  )}
                  {apifyData.profile.business_email && (
                    <a className="disc-profile__tag disc-profile__tag--link" href={`mailto:${apifyData.profile.business_email}`}>
                      <i className="bi bi-envelope-fill" /> {apifyData.profile.business_email}
                    </a>
                  )}
                  {apifyData.profile.business_phone && (
                    <a className="disc-profile__tag disc-profile__tag--link" href={`tel:${apifyData.profile.business_phone}`}>
                      <i className="bi bi-telephone-fill" /> {apifyData.profile.business_phone}
                    </a>
                  )}
                </div>
              )}
              {savedMsg && <p className="disc-saved-msg">{savedMsg}</p>}
            </div>
          </div>

          {/* Stats */}
          <div className="disc-stats">
            <StatCard label="Seguidores"        value={fmt(result.followers_count)} icon="bi-people-fill" />
            <StatCard label="Siguiendo"         value={fmt(result.follows_count)}   icon="bi-person-check-fill" />
            <StatCard label="Posts"             value={fmt(result.media_count)}     icon="bi-grid-fill" />
            {avgInteracciones != null && <StatCard label="Interacciones medias" value={fmt(avgInteracciones)} icon="bi-hand-thumbs-up-fill" />}
            {avgEng && <StatCard label="Eng. rate" value={avgEng} icon="bi-bullseye" />}
          </div>

          {apifyData && (apifyAvgLikes || apifyAvgComments || apifyAvgViews || apifyAvgReels) && (
            <div className="disc-stats disc-stats--apify">
              {apifyAvgLikes    != null && <StatCard label="Likes promedio"        value={fmt(apifyAvgLikes)}    icon="bi-heart-fill" />}
              {apifyAvgComments != null && <StatCard label="Comentarios promedio"  value={fmt(apifyAvgComments)} icon="bi-chat-fill" />}
              {apifyAvgViews    != null && <StatCard label="Vistas por post"       value={fmt(apifyAvgViews)}    icon="bi-eye-fill" />}
              {apifyAvgReels    != null && <StatCard label="Vistas reels prom."    value={fmt(apifyAvgReels)}    icon="bi-play-circle-fill" />}
            </div>
          )}

          {/* Posts recientes */}
          {posts.length > 0 && (
            <div className="disc-posts-section">
              <h3 className="disc-posts-title">Posts recientes</h3>
              <div className="disc-posts-grid">
                {posts.map(p => <PostThumb key={p.id} post={p} />)}
              </div>
            </div>
          )}

          {/* Posts patrocinados detectados */}
          {posts.length > 0 && (
            <div className="disc-paid-section">
              <h3 className="disc-posts-title">
                <i className="bi bi-megaphone" /> Patrocinados detectados
                <span className={`disc-paid-badge ${paidPosts.length > 0 ? 'disc-paid-badge--found' : 'disc-paid-badge--none'}`}>
                  {paidPosts.length} de {posts.length} posts
                </span>
              </h3>
              {paidPosts.length === 0 ? (
                <p className="disc-saved-empty">No se detectaron posts patrocinados en la muestra.</p>
              ) : (
                <div className="disc-paid-list">
                  {paidPosts.map(p => (
                    <a
                      key={p.id}
                      className="disc-paid-item"
                      href={p.permalink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {(p.thumbnail_url || p.media_url) && (
                        <img src={p.thumbnail_url || p.media_url} alt="" className="disc-paid-thumb" />
                      )}
                      <div className="disc-paid-info">
                        <p className="disc-paid-caption">
                          {(p.caption || '').slice(0, 120)}{p.caption?.length > 120 ? '…' : ''}
                        </p>
                        <div className="disc-paid-meta">
                          <span>{fmt(p.like_count)} ❤</span>
                          <span>{fmt(p.comments_count)} 💬</span>
                        </div>
                      </div>
                      <i className="bi bi-box-arrow-up-right disc-paid-link" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Etiquetas de cuentas (@menciones) */}
          <MencionesSection menciones={menciones} totalPosts={posts.length} />

          {/* Hashtags utilizados */}
          <HashtagsSection hashtags={hashtags} />

          {/* Evolución de likes */}
          <LikesChart posts={posts} />

          {/* ── Análisis Extra (Apify) ── */}
          {(apifyLoading || apifyError || apifyData) && (
          <div className="disc-apify-section">
            <h3 className="disc-apify-title">
              <i className="bi bi-stars" /> Análisis extra
            </h3>

            {apifyLoading && (
              <p className="disc-apify-note">
                <i className="bi bi-hourglass-split" /> Esto puede tomar 30–90 segundos mientras Apify obtiene los datos…
              </p>
            )}

            {apifyError && (
              <div className="disc-error" style={{ marginTop: '0.75rem' }}>
                <i className="bi bi-exclamation-triangle" /> {apifyError}
              </div>
            )}

            {apifyData && <ApifyResults data={apifyData} />}
          </div>
          )}

          {/* Perfiles relacionados */}
          {apifyData?.profile?.related_profiles?.length > 0 && (

            <div className="disc-paid-section">
              <h3 className="disc-posts-title">
                <i className="bi bi-people" /> Perfiles similares sugeridos por Instagram
              </h3>
              <div className="disc-related-grid">
                {apifyData.profile.related_profiles.map(p => (
                  <a
                    key={p.username}
                    className="disc-related-card"
                    href={`https://instagram.com/${p.username}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="disc-related-avatar">
                      {p.profile_pic_url
                        ? <img src={p.profile_pic_url} alt={p.username} />
                        : <i className="bi bi-person-circle" />}
                    </div>
                    <div className="disc-related-info">
                      <span className="disc-related-username">
                        @{p.username}
                        {p.is_verified && (
                          <svg viewBox="0 0 24 24" width="12" height="12" style={{marginLeft:'3px',verticalAlign:'middle'}}>
                            <path d="M12 2L14.09 8.26L21 9.27L16.18 13.97L17.45 21L12 17.77L6.55 21L7.82 13.97L3 9.27L9.91 8.26L12 2Z" fill="#3897f0" stroke="#3897f0" strokeWidth="1.5" strokeLinejoin="round"/>
                            <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </span>
                      {p.full_name && <span className="disc-related-name">{p.full_name}</span>}
                      {p.followers_count > 0 && (
                        <span className="disc-related-followers">
                          <i className="bi bi-people-fill" /> {fmt(p.followers_count)}
                        </span>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Perfiles guardados */}
      <div className="disc-saved-section">
        <h2 className="disc-saved-title">
          <i className="bi bi-bookmark-star" /> Guardados
          {saved.length > 0 && <span className="disc-saved-count">{saved.length}</span>}
        </h2>
        {loadingSaved ? (
          <Loading fullScreen={false} size="sm" />
        ) : saved.length === 0 ? (
          <p className="disc-saved-empty">Aún no has guardado ningún perfil.</p>
        ) : (
          <div className="disc-saved-list">
            {saved.map(p => (
              <SavedCard key={p.id} perfil={p} onDelete={handleDelete} onView={(u) => { setQuery(u); searchByUsername(u); }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
